import api from '../services/authService';
import axios from 'axios';

/**
 * Server-side AI Upload zip extraction client.
 *
 * Large zips (VBMS claim-file exports frequently exceed 4 GB) cannot be
 * unpacked in the browser: a single ArrayBuffer tops out around 2 GB and
 * JSZip's ZIP64 support is incomplete. This service uploads the raw zip
 * to the backend's staging area via S3 multipart presigned URLs, then the
 * backend extracts it server-side (stdlib zipfile — no size ceiling) and
 * routes every PDF through the normal upload/pipeline path.
 *
 * Flow: initiate -> (PUT parts to presigned URLs) -> complete -> poll status.
 */

// Zips at or above this size skip the client-side JSZip path entirely.
// Set to 1 byte so EVERY claim-file zip goes through the server: the
// server keeps the raw eFolder Express download (Matthew, 2026-10-09),
// which the browser-side unpack never could, and it is also the only path
// that records VA receipt dates from the original filenames. The
// client-side JSZip path below is no longer reached for zips.
export const SERVER_SIDE_ZIP_THRESHOLD_BYTES = 1;

// How many part URLs to request per batch. Batching keeps presigned URLs
// fresh on multi-hour uploads instead of minting them all up front.
const PART_URL_BATCH_SIZE = 20;

// Concurrent part PUTs. Parts are ~100 MB Blob slices (browsers stream
// them from disk), so 3 in flight balances throughput vs. connection load.
const CONCURRENT_PART_UPLOADS = 3;

const TERMINAL_STATUSES = new Set([
  'completed',
  'completed_with_errors',
  'failed',
  'aborted',
]);

export const initiateAiZipUpload = async ({ matterId, folderId, file }) => {
  const response = await api.post('/documents/ai-zip-upload/initiate/', {
    matter: matterId,
    folder: folderId || null,
    filename: file.name,
    size: file.size,
  });
  return response.data; // { job_id, part_size, part_count }
};

export const fetchPartUrls = async (jobId, partNumbers) => {
  const response = await api.post(
    `/documents/ai-zip-upload/${jobId}/part-urls/`,
    { part_numbers: partNumbers }
  );
  return response.data.urls; // { "1": url, ... }
};

export const completeAiZipUpload = async (jobId) => {
  const response = await api.post(`/documents/ai-zip-upload/${jobId}/complete/`);
  return response.data;
};

export const abortAiZipUpload = async (jobId) => {
  const response = await api.post(`/documents/ai-zip-upload/${jobId}/abort/`);
  return response.data;
};

export const getAiZipUploadStatus = async (jobId) => {
  const response = await api.get(`/documents/ai-zip-upload/${jobId}/status/`);
  return response.data;
};

/**
 * Upload one zip through the server-side path.
 *
 * @param {File} file - the zip file
 * @param {Object} options
 * @param {string|number} options.matterId
 * @param {string|null} options.folderId - destination folder id (no F prefix), or null for matter root
 * @param {Function} [options.onProgress] - called with 0-100 for the upload phase
 * @param {AbortSignal} [options.signal] - cancels part uploads; caller should also call abortAiZipUpload
 * @returns {Object} job payload from the complete call ({ job_id, status, ... })
 */
export const uploadZipServerSide = async (file, { matterId, folderId, onProgress, signal }) => {
  const { job_id: jobId, part_size: partSize, part_count: partCount } =
    await initiateAiZipUpload({ matterId, folderId, file });

  try {
    const partProgress = new Array(partCount).fill(0);
    const reportProgress = () => {
      if (!onProgress) return;
      const uploadedBytes = partProgress.reduce((a, b) => a + b, 0);
      onProgress(Math.min(99, Math.round((uploadedBytes / file.size) * 100)));
    };

    for (let batchStart = 1; batchStart <= partCount; batchStart += PART_URL_BATCH_SIZE) {
      const batchNumbers = [];
      for (
        let n = batchStart;
        n < batchStart + PART_URL_BATCH_SIZE && n <= partCount;
        n += 1
      ) {
        batchNumbers.push(n);
      }

      const urls = await fetchPartUrls(jobId, batchNumbers);

      // Upload the batch with limited concurrency. Each worker pulls the
      // next part number off the shared queue until the batch is drained.
      const queue = [...batchNumbers];
      const uploadNext = async () => {
        while (queue.length > 0) {
          const partNumber = queue.shift();
          const start = (partNumber - 1) * partSize;
          const chunk = file.slice(start, Math.min(start + partSize, file.size));
          await axios.put(urls[String(partNumber)], chunk, {
            signal,
            onUploadProgress: (event) => {
              partProgress[partNumber - 1] = event.loaded;
              reportProgress();
            },
          });
          partProgress[partNumber - 1] = chunk.size;
          reportProgress();
        }
      };
      const workers = Array.from(
        { length: Math.min(CONCURRENT_PART_UPLOADS, batchNumbers.length) },
        uploadNext
      );
      await Promise.all(workers);
    }

    const job = await completeAiZipUpload(jobId);
    if (onProgress) onProgress(100);
    return job;
  } catch (error) {
    // Best-effort server-side cleanup of the dangling multipart upload.
    try {
      await abortAiZipUpload(jobId);
    } catch {
      /* the backend's staging lifecycle handles stragglers */
    }
    throw error;
  }
};

// A backend deploy bounces the API for a minute or two, and long
// extractions routinely outlive a deploy window — so the poller must
// shrug off a burst of failed status checks instead of reporting the
// job failed while it's actually still running (or already done).
// 20 consecutive failures at ≥15 s spacing ≈ 5+ minutes of outage
// before we give up.
const MAX_CONSECUTIVE_POLL_FAILURES = 20;
const FAILURE_RETRY_MS = 15000;

/**
 * Poll a job until it reaches a terminal status.
 *
 * Transient status-check failures (deploy restarts, network blips) are
 * retried with a longer spacing; only a sustained outage rejects.
 *
 * @param {number} jobId
 * @param {Object} options
 * @param {Function} [options.onUpdate] - called with the job payload each poll
 * @param {number} [options.intervalMs]
 * @param {number} [options.timeoutMs] - give up (resolve with last payload) after this long
 * @param {number} [options.failureRetryMs] - spacing between retries during an outage
 * @returns {Object} final job payload
 */
export const pollAiZipJob = async (
  jobId,
  {
    onUpdate,
    intervalMs = 4000,
    timeoutMs = 4 * 60 * 60 * 1000,
    failureRetryMs = FAILURE_RETRY_MS,
  } = {}
) => {
  const startedAt = Date.now();
  let job = null;
  let consecutiveFailures = 0;

  while (true) {
    try {
      job = await getAiZipUploadStatus(jobId);
      consecutiveFailures = 0;
    } catch (error) {
      consecutiveFailures += 1;
      if (consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
        throw error;
      }
      await new Promise((resolve) =>
        setTimeout(resolve, Math.max(intervalMs, failureRetryMs))
      );
      continue;
    }

    if (TERMINAL_STATUSES.has(job.status)) break;
    if (onUpdate) onUpdate(job);
    if (Date.now() - startedAt > timeoutMs) break;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  if (onUpdate) onUpdate(job);
  return job;
};

/**
 * Human-readable one-line summary for a finished job — used in toasts.
 */
/**
 * The fields the upload drawer shows for a zip the server is working on:
 * whether it is still waiting its turn (and where in line) or extracting,
 * and how many documents are done.
 */
export const zipJobTrayFields = (job) => ({
  zipStage: job.status === 'uploaded' ? 'waiting' : 'extracting',
  queuePosition: job.queue_position ?? null,
  extractedCount: job.extracted_count ?? 0,
  totalPdfs: job.total_pdfs ?? 0,
  skippedCount: job.skipped_count ?? 0,
  failedCount: job.failed_count ?? 0,
});

export const summarizeAiZipJob = (job) => {
  if (job.status === 'failed') {
    return `Zip extraction failed: ${job.error_detail || 'unknown error'}`;
  }
  if (job.status === 'aborted') {
    return 'Zip extraction was cancelled.';
  }
  const parts = [`${job.extracted_count} PDF${job.extracted_count === 1 ? '' : 's'} uploaded`];
  if (job.skipped_count) parts.push(`${job.skipped_count} skipped`);
  if (job.failed_count) parts.push(`${job.failed_count} failed`);
  return parts.join(', ') + '.';
};
